import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of, zip } from 'rxjs';

import {
  MapService,
  MapServiceList,
  AnonMapService,
  MapServiceIdsList,
  MapServiceURLSlug,
} from './../../shared/models/map-services.model';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../shared/auth.service';
import { catchError, finalize, switchMap } from 'rxjs/operators';

@Injectable()
export class DatasetsService {
  datasetsLoading: boolean = false;
  datasetSlugs = [
    'euro-global-map',
    'euro-regional-map',
    'euro-dem',
    'cadastral-all',
    'pan-european-imagery',
    'open-gazetteer',
    'hvlsp'
  ];
  private loadedDatasets: BehaviorSubject<AnonMapService[]> = new BehaviorSubject<AnonMapService[]>(
    [],
  );
  constructor(private http: HttpClient, private authService: AuthService) {}

  /**
   * Returns headers with token
   * @returns {HttpHeaders}
   */
  getAuthHeaders(): HttpHeaders {
    return this.authService.getHeadersWithToken();
  }

  /**
   * Creates and saves a mapService
   * @param {Dataset} mapService - MapService to create
   * @returns {Observable<MapService>}
   */
  createMapService(mapService: MapService): Observable<MapService> {
    return this.http.put<MapService>(`${environment.apiBaseURL}/admin/mapservice`, mapService, {
      headers: this.getAuthHeaders(),
    });
  }

  /**
   * Get list of mapServices, filtered by optional search term
   * @param {page} Page number to return
   * @param {searchTerm} Filter list of mapservices on a search term
   * @returns {Observable<MapServiceList>}
   */
  getMapServices(page: number, searchTerm: string): Observable<MapServiceList> {
    let params = new HttpParams();
    params = params.append('page', page.toString());
    params = params.append('textSearch', searchTerm);
    return this.http.get<MapServiceList>(`${environment.apiBaseURL}/admin/mapservice/list`, {
      params: params,
      headers: this.getAuthHeaders(),
    });
  }

  /** Get list of mapServices, filtered by optional search term
   * @param {page} Page number to return
   * @param {searchTerm} Filter list of mapservices on a search term
   * @returns {Observable<MapServiceList>}
   */
  getMapServicesList(page: number, searchTerm: string): Observable<MapServiceList> {
    let params = new HttpParams();
    params = params.append('page', page.toString());
    params = params.append('textSearch', searchTerm);
    return this.http.get<MapServiceList>(`${environment.apiBaseURL}/mapservice/list`, {
      params: params,
    });
  }

  /**
   * Gets mapService matching mapServiceId
   * @param {number} mapServiceId
   * @returns {Observable<MapServiceList>}
   */
  getMapService(mapServiceId: number): Observable<MapService> {
    return this.http.get<MapService>(`${environment.apiBaseURL}/admin/mapservice/${mapServiceId}`, {
      headers: this.getAuthHeaders(),
    });
  }

  loadDatasets(): Observable<AnonMapService[]> {
    this.datasetsLoading = true;
    return zip(
      ...this.datasetSlugs.map((slug) => {
        return this.getMapServiceDetails(slug).pipe(
          catchError(() => {
            return of(null);
          }),
        );
      }),
    ).pipe(
      switchMap((datasets: AnonMapService[]) => of(datasets.filter(Boolean))),
      finalize(() => (this.datasetsLoading = false)),
    );
  }

  getDatasets(): BehaviorSubject<AnonMapService[]> {
    return this.loadedDatasets;
  }

  setDatasets(datasets: AnonMapService[]): void {
    this.loadedDatasets.next(datasets);
  }

  /**
   * Gets service matching service slug (unique identifier)
   * @param {number} serviceSlug
   * @returns {Observable<AnonMapService>}
   */
  getMapServiceDetails(serviceSlug: string): Observable<AnonMapService> {
    return this.http.get<AnonMapService>(`${environment.apiBaseURL}/mapservice/${serviceSlug}`);
  }

  /**
   * Gets mapservice id based on mapservice name
   * @param {mapServiceURLSlug} list of mapservice names
   * @returns {Observable<MapServiceIdsList>}
   */
  getMapServiceIds(mapServiceURLSlug: MapServiceURLSlug): Observable<MapServiceIdsList> {
    return this.http.post<MapServiceIdsList>(`${environment.apiBaseURL}/mapservice/list/ids`,
      mapServiceURLSlug,
      {headers: this.getAuthHeaders()}
    );
  }
}
